import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ProductNames } from '~/common/constants';
import { useServerInfo } from '~/common/contexts/serverInfo';
import { type TransInterpolation } from '~/common/types';

export interface FAQ {
  question: React.ReactNode;
  answer: React.ReactNode;
}

/**
 * Get the list of FAQs for the home page.
 */

const useFAQs = (): FAQ[] => {
  const { serverInfo } = useServerInfo();
  const { t } = useTranslation();

  return React.useMemo(() => {
    const list: FAQ[] = [
      {
        question: t(
          'Where should I submit my feedback or bug reports for Axe DevTools?'
        ),
        answer: (
          <p>
            <Trans>
              You can submit feedback by emailing{' '}
              <a href="mailto:helpdesk@deque.com">helpdesk@deque.com</a>. We
              value your input.
            </Trans>
          </p>
        )
      },
      {
        question: t('Why doesn’t “Axe DevTools” show up in Developer Tools?'),
        answer: (
          <>
            <p>
              <Trans shouldUnescape>
                Do to a known Chrome bug, which is fixed in version 102, the
                &quot;Axe DevTools&quot; might not always show up. To work
                around this issue:
              </Trans>
            </p>
            <ul>
              <li>{t('Open a new tab')}</li>
              <li>{t('Open up developer tools')}</li>
              <li>{t('Click “Axe DevTools”')}</li>
              <li>{t('Navigate to the site you would like to test')}</li>
            </ul>
          </>
        )
      },
      {
        question: t('Why am I getting errors, incorrect/inconsistent results?'),
        answer: (
          <Trans shouldUnescape>
            <p>
              Extensions that insert information into Web pages (like Grammarly,
              LastPass, Screenity and more) can cause analysis results using the
              browser extension to be inaccurate.
            </p>
            <p>
              There is a simple way to get around this problem: use an incognito
              window to analyze. By turning all other extensions OFF for
              incognito and turning the Axe extension ON in incognito mode, you
              can be sure that your results are a true reflection of the state
              of the page you are analyzing. To modify an extension’s setting
              for use in incognito mode, in Chrome:
            </p>
            <ol>
              <li>
                Go to <a href="chrome://extensions">chrome://extensions</a>
              </li>
              <li>Click the &quot;Details&quot; button</li>
              <li>
                Make sure that the &quot;Allow in Incognito&quot; switch is in
                the correct position (ON for Axe and OFF for all other
                extensions)
              </li>
            </ol>
          </Trans>
        )
      },
      {
        question: t('What browsers does the Axe DevTools Extension support?'),
        answer: (
          <p>
            {t(
              'At this time, Axe DevTools Extension supports the Chrome and Edge desktop browsers only.'
            )}
          </p>
        )
      },
      {
        question: t(
          'Why does the extension fail to analyze pages with embedded YouTube videos?'
        ),
        answer: (
          <Trans shouldUnescape>
            <p>
              YouTube and other Google applications like Documents, Drive etc.
              are shipped as embedded apps in the Chrome browser. This prevents
              other extensions from debugging those pages. Luckily you can
              uninstall the embedded application by following these simple
              steps:
            </p>
            <ol>
              <li>
                Go to <a href="chrome://apps">chrome://apps</a>
              </li>
              <li>Right click on the YouTube icon.</li>
              <li>Select &quot;delete&quot;</li>
              <li>Close the extension</li>
              <li>Refresh the page</li>
              <li>Open the extension</li>
            </ol>
            <p>You will now have access to all the functionality.</p>
          </Trans>
        )
      },
      {
        question: t('Is Axe DevTools available for native mobile apps?'),
        answer: (
          <Trans>
            <p>
              The Axe DevTools Extension only tests in a web pages in a browser.
              Developer tools which simulate mobile device viewports can be
              utilized to test mobile web applications.
            </p>
            <p>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.deque.com/axe/devtools/mobile-accessibility/?utm_medium=referral&utm_source=axe.deque.com&utm_campaign=axe-pro-faq"
              >
                {{ mobileProduct: ProductNames.mobile } as TransInterpolation}
              </a>{' '}
              can test native iOS and Android applications.
            </p>
          </Trans>
        )
      },
      {
        question: t(
          'I am getting a “Unable to connect” error and can’t analyze the page. What am I doing wrong?'
        ),
        answer: (
          <p>
            {t(
              'Please refresh the page and see if this fixes the problem. If that doesn’t fix the issue, close Chrome Developer Tools and open the extension again.'
            )}
          </p>
        )
      },
      {
        question: t('What data is Deque collecting from Axe DevTools users?'),
        answer: (
          <p>
            <Trans>
              Refer to the{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://docs.deque.com/devtools-server/4.0.0/en/data-collection"
              >
                Axe DevTools Data Collection
              </a>{' '}
              documentation.
            </Trans>
          </p>
        )
      },
      {
        question: t(
          'Can the Axe DevTools Extension be used by users who make use of screen readers?'
        ),
        answer: (
          <p>
            <Trans>
              Yes, the extension is designed to work with screen readers. We
              have{' '}
              <Link to="/screen-readers">
                a guide on how to get started that you’ll want to check out
              </Link>
              .
            </Trans>
          </p>
        )
      },
      {
        question: t(
          'Can the Axe DevTools Extension be used in incognito mode?'
        ),
        answer: (
          <p>
            <Trans shouldUnescape>
              Yes but you need to give the extension permission to run in
              incognito mode. You can do so by navigating to{' '}
              <a href="chrome://extensions">chrome://extensions</a>, clicking
              &quot;details&quot; on the extension, and changing the &quot;Allow
              in Incognito&quot; switch to ON.
            </Trans>
          </p>
        )
      },
      {
        question: t(
          'How do I stop running an in-progress Intelligent Guided Test?'
        ),
        answer: (
          <p>
            <Trans shouldUnescape>
              At any time during an IGT, you can click the &quot;Options&quot;
              menu and select &quot;Save progress &amp; quit&quot; which will
              save your current progress and return you to your saved test.
            </Trans>
          </p>
        )
      },
      {
        question: t(
          'How do I get Axe DevTools issues into my issue tracking system?'
        ),
        answer: (
          <p>
            <Trans shouldUnescape>
              You can bulk export all, or a filtered subset, of your issues
              using the export feature which can be found on both the saved test
              and issue summary pages. In addition, you can share specific
              issues by clicking the &quot;share issue&quot; button which will
              generate a full page report for the given issue and copy its link
              to your clipboard. This page will include useful information
              pertaining to the issue in addition to a screenshot (if
              screenshotting is enabled in the extension) of the issue.
            </Trans>
          </p>
        )
      }
    ];

    // No need to mention trial stuff when self provisioning is disabled.
    if (serverInfo?.isSelfProvisioningEnabled) {
      list.push({
        question: t(
          'I signed up for a trial but the trial doesn’t seem to be activated. Help!'
        ),
        answer: (
          <p>
            <Trans>
              After you create your account, you will need to go to{' '}
              <Link to="/plans?utm_campaign=webapp_plans">
                axe.deque.com/plans
              </Link>{' '}
              and click on “Get Started for Free” again to actually activate
              your trial. This step is needed even after you validate your
              email. We know this is a little confusing and we are working to
              improve this so you won’t have 1 extra irritating step.
            </Trans>
          </p>
        )
      });
    }
    return list;
  }, [serverInfo, t]);
};

export default useFAQs;
